<template>
  <div class="relative w-full mx-auto px-6 py-3 space-y-8">
    <!-- 🔵 Loader -->
    <div
      v-if="isLoading"
      class="absolute inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center z-50 rounded-xl"
    >
      <div class="animate-spin rounded-full h-10 w-10 border-t-4 border-blue-600"></div>
    </div>

    <!-- Header -->
    <div class="flex items-center justify-between flex-wrap gap-3">
      <div>
        <h1 class="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <i class="ri-user-settings-line text-blue-600"></i> Profile Settings
        </h1>
        <p class="text-gray-500 text-sm">Manage your personal information and account security.</p>
      </div>
    </div>

    <!-- Message -->
    <transition name="fade">
      <div
        v-if="msgTxt"
        :class="`p-3 rounded-lg text-sm ${
          msgType === 'success'
            ? 'bg-green-100 text-green-700'
            : 'bg-red-100 text-red-700'
        }`"
      >
        {{ msgTxt }}
      </div>
    </transition>

    <!-- Profile Form -->
    <div class="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 space-y-8">
      <!-- Section: Account Info -->
      <div>
        <h2 class="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <i class="ri-user-line text-blue-600"></i> Account Information
        </h2>

        <div class="grid md:grid-cols-2 gap-6">
          <!-- Username (Read Only) -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Username</label>
            <input
              type="text"
              v-model="user.name"
              disabled
              class="w-full border border-gray-200 rounded-lg px-4 py-2.5 bg-gray-50 text-gray-500 cursor-not-allowed"
            />
          </div>

          <!-- Email -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
            <input
              v-model="user.email"
              type="email"
              class="w-full border border-gray-200 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
          </div>
        </div>
      </div>

      <!-- Section: Security -->
      <div>
        <h2 class="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <i class="ri-shield-keyhole-line text-blue-600"></i> Security
        </h2>

        <div class="grid md:grid-cols-2 gap-6">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <div class="flex items-center gap-2">
              <input
                type="password"
                value="********"
                disabled
                class="w-full border border-gray-200 rounded-lg px-4 py-2.5 bg-gray-50 text-gray-500 cursor-not-allowed"
              />
              <button
                @click="showPasswordModal = true"
                class="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition"
              >
                Update
              </button>
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Account Created</label>
            <input
              type="text"
              :value="user.joined || 'N/A'"
              disabled
              class="w-full border border-gray-200 rounded-lg px-4 py-2.5 bg-gray-50 text-gray-500 cursor-not-allowed"
            />
          </div>
        </div>
      </div>

      <!-- Section: Actions -->
      <div class="pt-6 border-t border-gray-100 flex flex-wrap justify-between items-center gap-4">
        <button
          @click="saveChanges"
          class="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shadow-sm"
        >
          Save Changes
        </button>

        <button
          @click="confirmDelete"
          class="px-6 py-2.5 border border-red-500 text-red-600 rounded-lg hover:bg-red-50 transition"
        >
          Delete Account
        </button>
      </div>
    </div>

    <!-- 🔐 Password Modal -->
    <transition name="fade">
      <div
        v-if="showPasswordModal"
        class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
        @click.self="showPasswordModal = false"
      >
        <div
          class="bg-white rounded-2xl shadow-xl w-[90%] max-w-md p-6 relative animate-popUp"
        >
          <h3 class="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <i class="ri-lock-password-line text-blue-600"></i> Update Password
          </h3>
           <div
            v-if="isLoading1"
            class="absolute inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center z-50 rounded-xl"
            >
            <div class="animate-spin rounded-full h-10 w-10 border-t-4 border-blue-600"></div>
            </div>

           <div
        v-if="msgTxt1"
        :class="`p-3 rounded-lg text-sm ${
          msgType1 === 'success'
            ? 'bg-green-100 text-green-700'
            : 'bg-red-100 text-red-700'
        }`"
      >
        {{ msgTxt1 }}
      </div>

          <div class="space-y-4">
            <input
              v-model="oldPass"
              type="password"
              placeholder="Current Password"
              class="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm"
            />
            <input
              v-model="newPass"
              type="password"
              placeholder="New Password"
              class="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm"
            />
            <input
              v-model="confirmPass"
              type="password"
              placeholder="Confirm Password"
              class="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm"
            />
          </div>

          <div class="flex justify-end gap-3 mt-5">
            <button
              @click="showPasswordModal = false"
              class="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition"
            >
              Cancel
            </button>
            <button
              @click="changePassword"
              class="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Custom Confirmation Dialog -->
<transition name="fade-scale">
  <div
    v-if="showDeleteDialog"
    class="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[9999]"
  >
    <div
      class="bg-white rounded-2xl shadow-2xl w-[90%] max-w-md p-6 relative animate-popUp"
    >
      <!-- Header -->
      <div class="flex items-center gap-3 mb-3">
        <div
          class="w-10 h-10 flex items-center justify-center bg-red-100 text-red-600 rounded-full"
        >
          <i class="ri-error-warning-line text-xl"></i>
        </div>
        <h3 class="text-lg font-semibold text-gray-800">
          Confirm Account Deletion
        </h3>
      </div>

      <!-- Message -->
      <p class="text-gray-600 text-sm leading-relaxed mb-6">
        Are you sure you want to delete your account?  
        <b>This action cannot be undone.</b>
      </p>

      <!-- Buttons -->
      <div class="flex justify-end gap-3">
        <button
          @click="showDeleteDialog = false"
          class="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition"
        >
          Cancel
        </button>
        <button
          @click="confirmDeleteAction"
          class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
        >
          Delete
        </button>
      </div>
    </div>
  </div>
</transition>

  </div>
</template>

<script>
import { chPassNow, getMyDt,delAcc,upEmail } from "@/apis/webapis";

export default {
  name: "ProfileSettings",
  data() {
    return {
        showDeleteDialog: false,
      isLoading: false,
      isLoading1: false,
      showPasswordModal: false,
      user: { name: "", email: "", joined: "" },
      oldPass: "",
      newPass: "",
      confirmPass: "",
      msgTxt: "",
      msgType: "",
      msgTxt1: "",
      msgType1: "",
    };
  },
  async mounted() {
    const ldata = JSON.parse(sessionStorage.getItem("ldata") || "{}");
    if (ldata?.user_id) await this.loadUser(ldata.user_id);
  },
  methods: {
    async loadUser(user_id) {
      this.isLoading = true;
      try {
        const res = await getMyDt(user_id);
        if (res.status && res.user_dt) {
          this.user = {
            name: res.user_dt.name,
            email: res.user_dt.email,
            joined: res.user_dt.user_cre_datetime || "N/A",
            user_id: user_id,
          };
        }
      } finally {
        this.isLoading = false;
      }
    },
    async changePassword() {
     this.msgTxt1 = "";
    this.msgType1 = "";
     this.msgTxt = "";
    this.msgType = "";

    // 1️⃣ Empty field check
    if (!this.oldPass || !this.newPass || !this.confirmPass) {
        this.msgTxt1 = "All password fields are required. Please fill them before continuing.";
        this.msgType1 = "danger";
        return;
    }

    // 2️⃣ Minimum password length
    if (this.newPass.length < 6) {
        this.msgTxt1 = "New password must be at least 6 characters long.";
        this.msgType1 = "danger";
        return;
    }

    if (this.oldPass === this.newPass) {
        this.msgTxt1 = "Your new password cannot be the same as the old password.";
        this.msgType1 = "danger";
        return;
    }

     if (this.newPass !== this.confirmPass) {
        this.msgTxt1 = "New password and confirm password do not match.";
        this.msgType1 = "danger";
        return;
    }

    this.msgTxt1 = "";
    this.msgType1 = "";

      this.isLoading1 = true;
      try {
        const res = await chPassNow(this.oldPass, this.newPass, this.user.user_id);
        
        if(res.status){
            this.msgTxt = res.success || "Password updated successfully!";
        this.msgType = res.status ? "success" : "danger";
        }
        else{
            this.msgTxt1 = res.success || "Password updated successfully!";
        this.msgType1 = res.status ? "success" : "danger";
        }
        if (res.status) this.showPasswordModal = false;
      } finally {
        this.isLoading1 = false;
      }
    },
    async saveChanges() {
    //   this.msgTxt = "Your profile has been updated successfully.";
    //   this.msgType = "success";

     this.msgTxt = "";
    this.msgType = "";

      this.isLoading = true;
      try {
        const res = await upEmail(this.user.user_id,this.user.email);
        
        this.msgTxt = res.success || "Your email updated successfully!";
        this.msgType = res.status ? "success" : "danger";
        
        // if (res.status) {
            
        // }
      } finally {
        this.isLoading = false;
      }
    },
   confirmDelete() {
    // 🔹 Open the custom confirmation dialog
    this.showDeleteDialog = true;
  },
  async confirmDeleteAction() {

    this.msgTxt = "";
    this.msgType = "";

      this.isLoading = true;
      try {
        const res = await delAcc(this.user.user_id);
        
        this.msgTxt = res.success || "Password updated successfully!";
        this.msgType = res.status ? "success" : "danger";
        
        if (res.status) {
            this.showDeleteDialog = false;
            this.msgTxt = "Your account deletion request has been submitted.";
            this.msgType = "success";
            sessionStorage.removeItem("ldata");
            window.location.reload();
        }
      } finally {
        this.isLoading = false;
      }

    // 🔹 Perform actual deletion (replace with API call if needed)
    
  },
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.animate-popUp {
  animation: popUp 0.3s ease-out;
}
@keyframes popUp {
  from {
    transform: scale(0.96);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
